<template>
  <div
    id="element"
    class="profile-container"
  >
    <div class="profile-container--item ">
      <Header/>
    </div>
    <div class="section-gray">
      <div class="profile-header">
        <label for="avatar">
          <div
            v-if="user.profilePicture"
            class="user-img"
          >
            <div v-lazy-container="{ selector: 'img' }">
              <img
                :data-src="user.profilePicture"
                :data-loading="defaultImg"
                data-test="user-profile--display-user-image-profile"
                class="profile"
              >
            </div>
            <img
              class="camera"
              src="/static/svg/camera.svg"
            >
          </div>
          <div
            v-else
            class="border"
          >
            <img
              :src="defaultImg"
              class="profile"
            >
            <img
              class="camera"
              src="/static/svg/camera.svg"
            >
          </div>
        </label>
        <input
          v-show="false"
          id="avatar"
          ref="avatarFile"
          type="file"
          name="avatar"
          accept="image/png, image/jpeg"
          data-test="user-profile--user-profile-input"
          @change="previewFile"
        >
      </div>
      <div class="profile-title">
        {{ $t('profile.profile') }}
      </div>
      <div class="section-white">
        <div class="columns">
          <div class="column">
            <b-field :label="$t('profile.display_name')">
              <b-input
                v-validate="'required'"
                v-if="isUserDataExist"
                v-model="user.displayName"
                name="display name"
                data-test="user-profile--display-name-field"
                type="text"
              />
              <b-skeleton
                v-else
                height="35px"
              />
            </b-field>
            <span class="is-invalid">{{ errors.first('display name') }}</span>
          </div>
          <div class="column">
            <b-field :label="$t('profile.email_address')">
              <b-input
                v-if="isUserDataExist"
                :value="user.email"
                type="email"
                disabled
              />
              <b-skeleton
                v-else
                height="35px"
              />
            </b-field>
          </div>
        </div>
        <div class="columns">
          <div
            id="user-profile-token-balance"
            class="column"
          >
            <b-field :label="$t('profile.kul_balance')">
              <b-input
                v-if="isUserDataExist"
                :value="kulapTokenBalance"
                type="text"
                disabled
              />
              <b-skeleton
                v-else
                height="35px"
              />
            </b-field>
          </div>
        </div>
        <b-button
          :disabled="!isUserDataExist"
          :loading="isLoadingSubmit"
          class="submit-btn"
          data-test="user-profile--submit-btn"
          @click="submit"
        >
          {{ $t('buttons.submit') }}
        </b-button>
        <div id="section-social-connect"/>
        <div v-if="isUserDataExist">
          <ConnectWithFacebook
            :user="user"
            :can-unlink="user.accountType !== 'FACEBOOK'"
            @onSignInFacebookSuccess="onSignInFacebookSuccess"
            @onSignInFacebookError="onSignInFacebookError"
            @onDisconnect="unlinkSocial('facebook')"
          />
          <ConnectWithGoogle
            :user="user"
            :can-unlink="user.accountType !== 'GOOGLE'"
            :google-client-id="googleClientId"
            @onSignInGoogleSuccess="onSignInGoogleSuccess"
            @onSignInGoogleError="onSignInGoogleError"
            @onDisconnect="unlinkSocial('google')"
          />
          <ActivityTemplate
            :is-status-claim="user.kycStatus === 'APPROVED'"
            data-test="user-profile--kyc-template"
          >
            <template v-slot:icon>
              <img
                src="/static/svg/icon-kyc-profile.svg"
                class="pd-l-7px"
              >
            </template>
            <template v-slot:title>
              <span class="head">{{ $t('menu.kyc') }}</span>
            </template>
            <template v-slot:description>
              <p>{{ $t('profile.kyc_detail') }}</p>
              <p class="warning-text">{{ $t('profile.exchange_condition') }}</p>
            </template>
            <template v-slot:button>
              <div
                :class="[
                  'text-btn',
                  {
                    'unverified': user.kycStatus === 'UNVERIFIED',
                    'verifying': user.kycStatus === 'VERIFYING',
                    'request-change': user.kycStatus === 'REQUEST_CHANGE',
                    'rejected': user.kycStatus === 'REJECTED',
                    'approved': user.kycStatus === 'APPROVED',
                  }
                ]"
                data-test="user-profile--verifiaction-button"
                @click="goToPageKyc"
              >
                {{ $t(`profile.${user.kycStatus.toLowerCase()}`) }}
              </div>
            </template>
          </ActivityTemplate>
          <ActivityTemplate
            :is-status-claim="user.hasOwnProperty('suitabilityTestPoint')"
            data-test="user-profile--suit-test-template"
          >
            <template v-slot:icon>
              <img
                src="/static/svg/icon-suitability-test.svg"
                class="pd-l-7px"
              >
            </template>
            <template v-slot:title>
              <span class="head">{{ $t('menu.suit_test') }}</span>
            </template>
            <template v-slot:description>
              <p>{{ $t('profile.suit_test_detail') }}</p>
              <p class="warning-text">{{ $t('profile.exchange_condition') }}</p>
            </template>
            <template v-slot:button>
              <div
                class="text-btn"
                data-test="user-profile--suit-test-button"
                @click="$router.push({ name: 'SuitabilityTest'})"
              >
                {{ $t('profile.take_a_test') }}
              </div>
            </template>
          </ActivityTemplate>
          <ActivityTemplate
            :is-status-claim="user.hasOwnProperty('knowledgeTestPoint')"
            data-test="user-profile--knowledge-test-template"
          >
            <template v-slot:icon>
              <img
                src="/static/svg/icon-knowledge-test.svg"
                class="pd-l-7px"
              >
            </template>
            <template v-slot:title>
              <span class="head">{{ $t('menu.knowledge_test') }}</span>
            </template>
            <template v-slot:description>
              <p>{{ $t('profile.knowledge_test_detail') }}</p>
              <p class="warning-text">{{ $t('profile.exchange_condition') }}</p>
            </template>
            <template v-slot:button>
              <div
                class="text-btn"
                data-test="user-profile--knowledge-test-button"
                @click="$router.push({ name: 'KnowledgeTest'})"
              >
                {{ $t('profile.take_a_test') }}
              </div>
            </template>
          </ActivityTemplate>
          <ActivityTemplate
            :is-status-claim="isSubscribeNewsLetter"
            data-test="user-profile--subscribe-template"
          >
            <template v-slot:icon>
              <img
                src="/static/svg/icon-newsletter.svg"
                class="pd-l-7px"
              >
            </template>
            <template v-slot:title>
              <span class="head">{{ $t('profile.subscribe') }}</span>
            </template>
            <template v-slot:description>
              <p>{{ $t('profile.subscribe_detail') }}</p>
            </template>
            <template v-slot:button>
              <b-switch
                v-model="user.isSubscribeNews"
                data-test="user-profile--subscribe-toggle"
                @input="onSwitchSubscribe"
              />
            </template>
          </ActivityTemplate>
          <ActivityTemplate
            :is-status-claim="user.isEnabled2FA"
            data-test="user-profile--2fa-template"
          >
            <template v-slot:icon>
              <img
                src="/static/img/icon-google-2FA.png"
                class="pd-l-7px w-33px"
              >
            </template>
            <template v-slot:title>
              <span class="head">{{ $t('profile.two_fact') }}</span>
            </template>
            <template v-slot:description>
              <p>{{ $t('profile.two_fact_detail') }}</p>
            </template>
            <template v-slot:button>
              <b-switch
                v-model="input2FAAuth"
                @input="onSwitch2FAToggle"
              />
            </template>
          </ActivityTemplate>
        </div>
        <!-- User Data is not exist -->
        <div
          v-else
          class="columns"
        >
          <div class="column">
            <b-skeleton
              :count="7"
              height="30px"
            />
          </div>
          <div class="column">
            <b-skeleton
              :count="7"
              height="30px"
            />
          </div>
          <div class="column">
            <b-skeleton
              :count="7"
              height="30px"
            />
          </div>
        </div>
      </div>
    </div>
    <b-modal :active.sync="isShowVerify2FAModal">
      <div class="modal-card">
        <Verify2FAModal
          @submit="onSubmitDisable2FA"
          @close="onCloseDisable2FAModal"
        />
      </div>
    </b-modal>
    <div class="profile-container--item">
      <Footer/>
    </div>
  </div>
</template>

<script>
import Service from './service'
import { mapState } from 'vuex'
import { kulapApi } from '@/resources'
import Header from '@/features/Header'
import ActivityTemplate from '@/features/ActivityTemplate'
import Footer from '@/components/Footer'
import ConnectWithGoogle from '@/components/ConnectWithGoogle'
import ConnectWithFacebook from '@/components/ConnectWithFacebook'
import Enable2FAModal from '@/components/Enable2FAModal'
import Verify2FAModal from '@/components/Verify2FAModal'
import CongratsToGetReward from '@/components/CongratsToGetReward'
import cookie from '@/utils/cookie'
import { calTotalKULToken, showToastError } from '@/utils/helper'
import { GOOGLE_CLIENT_ID } from '@/constants'

export default {
  name: 'UserProfile',
  components: {
    Header,
    Footer,
    ActivityTemplate,
    ConnectWithGoogle,
    ConnectWithFacebook,
    Enable2FAModal,
    Verify2FAModal
  },
  data: () => ({
    service: null,
    googleClientId: GOOGLE_CLIENT_ID,
    input2FAAuth: false,
    isShowVerify2FAModal: false,
    isLoadingSubmit: false,
    defaultImg: require('../../assets/svg/default-user-profile.svg')
  }),
  computed: {
    ...mapState({
      user: state => state.user
    }),
    kulapTokenBalance () {
      if (this.user && this.user.rewards) {
        return `${calTotalKULToken(this.user.rewards)} Token`
      } else {
        return 0
      }
    },
    isUserDataExist () {
      return Boolean(Object.keys(this.user).length)
    },
    isSubscribeNewsLetter () {
      return Boolean(this.user.isSubscribeNews)
    }
  },
  async created () {
    if (!cookie.get('user') || !cookie.get('accessToken')) {
      this.$router.push({ name: 'Login', query: { next: 'UserProfile' } })
      return
    }
    this.service = new Service({
      store: this.$store,
      kulapApi
    })
    const user = await this.service.getUserProfile()
    if (!user.isActive) {
      this.$router.push({ name: 'Exchange' })
    }
    this.input2FAAuth = Boolean(this.user.isEnabled2FA)
  },
  mounted () {
    const currentRoute = this.$router.currentRoute
    const idToScrollTo = currentRoute.hash
    this.$nextTick(() => {
      if (idToScrollTo && document.querySelector(idToScrollTo)) {
        document.querySelector(idToScrollTo).scrollIntoView({ behavior: 'smooth' })
      }
    })
  },
  methods: {
    async submit () {
      this.isLoadingSubmit = true
      const validateFieldResult = await this.$validator.validate()
      if (validateFieldResult) {
        try {
          const params = {
            displayName: this.user.displayName
          }
          const result = await this.service.updateUserProfile(params)
          if (result.success) {
            this.$buefy.toast.open({
              message: 'Update user profile success.',
              type: 'is-success'
            })
          }
          await this.congratsConnectSocial('SubscribeNewsLetter')
        } catch (e) {
          showToastError(e)
        }
        this.isLoadingSubmit = false
      }
    },
    async onSignInFacebookSuccess (response) {
      try {
        const params = {
          facebookId: response.authResponse.userID,
          facebook: {
            uid: response.authResponse.userID,
            accessToken: response.authResponse.accessToken
          }
        }
        await this.service.updateUserProfile(params)
        await this.congratsConnectSocial('ConnectWithFacebook')
      } catch (e) {
        showToastError(e)
      }
    },
    onSignInFacebookError (e) {
      if (e && e.status !== 'unknown') showToastError({ message: `Sorry, something went wrong.` })
    },
    async onSignInGoogleSuccess (googleUser) {
      try {
        const { id_token: googleIdToken } = await googleUser.getAuthResponse()
        await this.service.updateUserProfile({ googleIdToken })
        await this.congratsConnectSocial('ConnectWithGoogle')
      } catch (e) {
        showToastError(e)
      }
    },
    onSignInGoogleError (e) {
      if (e.error && e.error !== 'popup_closed_by_user') showToastError({ message: `Sorry, something went wrong.` })
    },
    async unlinkSocial (social) {
      try {
        await this.service.unlinkSocial(social)
        await this.service.getUserProfile()
      } catch (e) {
        showToastError(e)
      }
    },
    async congratsConnectSocial (rewardName) {
      const data = await this.service.getUserProfile()
      if (data.rewards.length) {
        const findRewards = data.rewards.filter(e => e.name === rewardName && e.options.isOpenCongratsModal)
        if (findRewards.length) {
          let subTitleRewardName = ''
          if (rewardName === 'ConnectWithGoogle') subTitleRewardName = this.$t('messages.google')
          else if (rewardName === 'ConnectWithFacebook') subTitleRewardName = this.$t('messages.facebook')
          const subTitle = rewardName === 'SubscribeNewsLetter' ? this.$t('messages.thanks_for_subscribe') : this.$t('messages.thanks_for_connect_social_with_us', [subTitleRewardName])
          this.$buefy.modal.open({
            parent: this,
            component: CongratsToGetReward,
            hasModalCard: true,
            props: {
              tokenAmount: calTotalKULToken(findRewards),
              subTitle
            },
            canCancel: ['escape', 'outside']
          })
          const changeRewardsOption = findRewards.map(e => ({ ...e, options: { isOpenCongratsModal: false } }))
          await kulapApi.putRewardsOption(changeRewardsOption)
          await this.service.getUserProfile()
        }
      }
    },
    async previewFile () {
      try {
        const url = await this.service.updateProfilePicture(this.$refs.avatarFile.files[0])
        await this.service.getUserProfile()
        this.user.profilePicture = `${url}?${Math.random()}`
      } catch (e) {
        showToastError(e)
      }
    },
    async onSwitchSubscribe () {
      try {
        const swapStatus = { isSubscribeNews: this.user.isSubscribeNews }
        await this.service.updateUserProfile(swapStatus)
        await this.congratsConnectSocial('SubscribeNewsLetter')
      } catch (e) {
        showToastError(e)
      }
    },
    async onSwitch2FAToggle () {
      try {
        if (!this.user.isEnabled2FA && this.input2FAAuth) { // Enable
          const { secret, url } = await this.service.generate2FACode()
          this.openEnable2FAModal(secret.base32, url)
        } else if (this.user.isEnabled2FA && !this.input2FAAuth) { // Disabled
          this.isShowVerify2FAModal = true
        }
      } catch (e) {
        showToastError(e)
      }
    },
    openEnable2FAModal (backUpKey, url) {
      this.$buefy.modal.open({
        parent: this,
        component: Enable2FAModal,
        hasModalCard: true,
        canCancel: ['escape', 'outside', 'x'],
        events: {
          submit: this.onSubmitEnable2FA,
          close: () => {
            this.input2FAAuth = false
          }
        },
        props: { backUpKey, url },
        scroll: 'clip'
      })
    },
    async onSubmitEnable2FA (code) {
      try {
        const result = await this.service.enable2FA(code)
        if (result.success) {
          this.$buefy.toast.open({
            message: '2FA Enabled !',
            type: 'is-success'
          })
          this.input2FAAuth = true
          await this.service.getUserProfile()
        } else {
          this.input2FAAuth = false
        }
      } catch (e) {
        this.input2FAAuth = false
        showToastError(e)
      }
    },
    async onSubmitDisable2FA (code) {
      this.isShowVerify2FAModal = false
      try {
        const result = await this.service.disable2FA(code)
        if (result.success) {
          this.input2FAAuth = false
          await this.service.getUserProfile()
        } else {
          this.input2FAAuth = true
        }
      } catch (e) {
        this.input2FAAuth = true
        showToastError(e)
      }
    },
    onCloseDisable2FAModal () {
      this.input2FAAuth = true
    },
    isRewardsExist (rewardName) {
      return Boolean(this.user.rewards && this.user.rewards.find(e => e.name === rewardName))
    },
    goToPageKyc () {
      if (this.user.kycStatus !== 'UNVERIFIED') {
        this.$router.push({ name: 'SummaryOfKYC' })
      } else {
        this.$router.push({ name: 'KYC' })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-card {
  background-color: #fff;
  border-radius: 5px;
  padding: 30px 40px;
  margin: 0 auto !important;
}
.profile-container {
  float: left;
  width: 100%;
  min-height: 100vh;

  &--item {
    float: left;
    width: 100%;
  }
}
.profile-header {
  width: 100%;
  height: 250px;
  background-image: url('/static/img/bg-profile.jpg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  margin-top: -32px;
  position: relative;

  label {
    cursor: pointer;

    img {
      // background-color: #fff;
      object-fit: cover;
    }
  }

  .user-img {
    width: 140px;
    height: 140px;
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    box-shadow: 0 2px 6px -1px rgba(0, 0, 0, 0.5);
    .camera {
      display: none;
      z-index: 99;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      box-shadow: 0 2px 6px -1px rgba(0, 0, 0, 0.5);
    }
    .profile {
      width: 100%;
      height: 140px;
      border-radius: 50%;
    }
    &:hover {
      opacity: 0.8;
      background-color: #000000;
    }
  }

  .user-img:hover .camera {
    display: unset !important;
  }

  .border {
    width: 140px;
    height: 140px;
    background: #fff;
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 32px;
    border-radius: 50%;
    box-shadow: 0 2px 6px -1px rgba(0, 0, 0, 0.5);

    .camera {
      display: none;
      z-index: 99;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      box-shadow: 0 2px 6px -1px rgba(0, 0, 0, 0.5);
    }

    .profile {
      width: 75px;
      height: 75px;
    }

    &:hover {
      opacity: 0.8;
      background-color: #000000;
    }
  }

  .border:hover .camera {
    display: unset !important;
  }
}
.section-gray {
  float: left;
  width: 100%;
  padding-bottom: 170px;
  text-align: center;
}
.profile-title {
  margin: 100px 0 25px 0;
  font-size: 30px;
  font-weight: bold;
  text-align: center;
  color: #000000;
}
.section-white {
  display: inline-block;
  padding: 32px 47px;
  width: 100%;
  max-width: 800px;
  border-radius: 5px;
  box-shadow: 0 2px 4px -3px rgba(0, 0, 0, 0.5);
  background-color: #ffffff;
  text-align: left;
}
.sub-title {
  font-family: Roboto;
  font-size: 21px;
  font-weight: bold;
  color: #000000;
  &.customer-info {
    float: left;
  }
}
.subscribe-container {
  float: left;
  width: 100%;
  margin-top: 30px;
}
.subscribe-custom-box {
  float: left;
}
.subscribe-text {
  float: left;
}
.divider {
  float: left;
  width: 100%;
  border: solid 1px #e4e4e4;
  margin: 40px 0;
}
.kyc-container {
  font-family: Roboto;
  font-size: 15px;
  font-weight: bold;
  letter-spacing: 0.3px;
  color: #000000;
  margin-top: 30px;
  float: left;
  width: 100%;

  &--title-status {
    float: left;
  }
  &--status {
    float: left;
    border-radius: 25px;
    border: 1px solid #f15c25;
    background-color: #fff;
    color: #f15c25;
    padding: 4px 17px;
    text-align: center;
    font-size: 13px;
    margin-left: 10px;
    &.verifying {
      color: #0559af;
      border-color: #0559af;
    }
    &.request-change {
      color: #dc3731;
      border-color: #dc3731;
    }
    &.approved {
      color: #43a047;
      border-color: #43a047;
    }
    &.rejected {
      color: #dc3731;
      border-color: #dc3731;
    }
  }
}
.info-text {
  font-family: Roboto;
  font-size: 16px;
  color: #262626;
  margin-top: 10px;
  float: left;
  span {
    color: #2193ff;
    cursor: pointer;
    font-weight: bold;
    text-decoration: underline;
  }
}
.submit-btn {
  display: flex;
  justify-content: center;
  width: 100%;
  width: 163px;
  height: 45px;
  margin: 40px auto 100px;
  border-radius: 22.5px;
  border: unset;
  background-color: #2193ff;
  color: #fff;
  font-size: 15px;
  font-weight: bold;
  letter-spacing: 0.4px;
  padding: 13px 0;
  text-align: center;
  cursor: pointer;
}
.pd-l-7px {
  padding-left: 7px;
}
.w-33px {
  width: 33px;
}
.text-btn {
  margin: 0 5px 0 15px;
  vertical-align: top;
  font-size: 13px;
  color: #2193ff;
  font-weight: bold;
  text-decoration: underline;
  &:hover {
    color: #0079fd;
    cursor: pointer;
  }
  &.unverified {
    color: #2193ff;
  }
  &.verifying {
    color: #2193ff;
  }
  &.request-change {
    color: #dc3731;
  }
  &.approved {
    color: #43a047;
  }
  &.rejected {
    color: #dc3731;
  }
}

/* Mobile */
@media only screen and (max-width: 768px) {
  .section-gray {
    padding-bottom: 1rem;
  }
  .section-white {
    padding: 20px;
    max-width: 95vw;
  }
  .profile-header {
    height: 180px;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/UserProfile/index.vue